/**
 * Global Video Modal Component
 * Singleton class for managing video modals across all sections
 * Supports Vimeo and Shopify-hosted videos
 * 
 * Usage:
 *   window.VideoModal.open({
 *     type: 'vimeo',           // 'vimeo' or 'shopify'
 *     vimeoId: '123456789',    // Required for vimeo type
 *     shopifyVideoSrc: '',     // Required for shopify type (video source URL)
 *     size: 'fullscreen',      // 'fullscreen' or 'smaller'
 *     triggerElement: element  // Element that triggered the modal (for focus restore)
 *   });
 * 
 *   window.VideoModal.close();
 * 
 * Events:
 *   document.addEventListener('videoModalClosed', (e) => {
 *     // e.detail contains { triggerElement }
 *   });
 */

class VideoModalManager {
  constructor() {
    this.modal = null;
    this.isOpen = false;
    this.currentConfig = null;
    this._previouslyFocusedElement = null;
    this._handleFocusTrap = this._handleFocusTrap.bind(this);
    this._handleEscape = this._handleEscape.bind(this);
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._init());
    } else {
      this._init();
    }
  }

  _init() {
    this.modal = document.getElementById('GlobalVideoModal');
    if (!this.modal) {
      console.warn('VideoModal: Global modal element not found');
      return;
    }

    this.backdrop = this.modal.querySelector('[data-modal-backdrop]');
    this.closeButton = this.modal.querySelector('[data-modal-close]');
    this.content = this.modal.querySelector('[data-modal-content]');
    this.iframeWrapper = this.modal.querySelector('[data-modal-iframe-wrapper]');
    this.videoElement = this.modal.querySelector('[data-modal-video]');

    // Set up event listeners
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    // Global escape key listener
    document.addEventListener('keydown', this._handleEscape);
  }

  /**
   * Open the video modal
   * @param {Object} config - Configuration object
   * @param {string} config.type - 'vimeo' or 'shopify'
   * @param {string} config.vimeoId - Vimeo video ID (required for vimeo type)
   * @param {string} config.shopifyVideoSrc - Shopify video source URL (required for shopify type)
   * @param {string} config.size - 'fullscreen' or 'smaller' (default: 'fullscreen')
   * @param {HTMLElement} config.triggerElement - Element that triggered the modal
   */
  open(config) {
    if (!this.modal) {
      console.warn('VideoModal: Modal not initialized');
      return;
    }

    if (this.isOpen) {
      this.close();
    }

    this.currentConfig = config;
    this._previouslyFocusedElement = config.triggerElement || document.activeElement;

    // Set modal size
    const size = config.size || 'fullscreen';
    this.modal.classList.remove('video-modal--fullscreen', 'video-modal--smaller');
    this.modal.classList.add(`video-modal--${size}`);

    // Show modal
    this.modal.removeAttribute('hidden');
    
    // Trigger reflow before adding class for transition
    this.modal.offsetHeight;
    this.modal.classList.add('is-active');
    this.isOpen = true;

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Set page content inert
    this._setPageInert(true);

    // Load video
    this._loadVideo(config);

    // Set up focus trap
    document.addEventListener('keydown', this._handleFocusTrap);

    // Focus the close button
    this.modal.setAttribute('tabindex', '-1');
    this.modal.focus();
    
    if (this.closeButton) {
      setTimeout(() => this.closeButton.focus(), 100);
    }
  }

  /**
   * Close the video modal
   */
  close() {
    if (!this.modal || !this.isOpen) return;

    // Stop video
    this._stopVideo();

    // Remove focus trap
    document.removeEventListener('keydown', this._handleFocusTrap);

    // Remove inert from page content
    this._setPageInert(false);

    // Hide modal
    this.modal.classList.remove('is-active');
    
    setTimeout(() => {
      this.modal.setAttribute('hidden', '');
      this.modal.removeAttribute('tabindex');
      this.modal.classList.remove('video-modal--fullscreen', 'video-modal--smaller');
    }, 300);

    // Restore body scroll
    document.body.style.overflow = '';

    this.isOpen = false;

    // Dispatch close event
    const closeEvent = new CustomEvent('videoModalClosed', {
      detail: {
        triggerElement: this._previouslyFocusedElement
      }
    });
    document.dispatchEvent(closeEvent);

    // Restore focus to trigger element
    if (this._previouslyFocusedElement && typeof this._previouslyFocusedElement.focus === 'function') {
      this._previouslyFocusedElement.focus();
    }

    this._previouslyFocusedElement = null;
    this.currentConfig = null;
  }

  /**
   * Load video content based on type
   */
  _loadVideo(config) {
    const { type, vimeoId, shopifyVideoSrc } = config;

    // Clear any existing content
    if (this.iframeWrapper) {
      this.iframeWrapper.innerHTML = '';
    }

    if (type === 'vimeo' && vimeoId) {
      this._createVimeoIframe(vimeoId);
    } else if (type === 'shopify' && shopifyVideoSrc) {
      this._createShopifyVideo(shopifyVideoSrc);
    }
  }

  /**
   * Create Vimeo iframe
   */
  _createVimeoIframe(vimeoId) {
    if (!this.iframeWrapper) return;

    const videoSrc = `https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0&dnt=1&like=0&watchlater=0&share=0&vimeo_logo=0`;
    
    const iframe = document.createElement('iframe');
    iframe.src = videoSrc;
    iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
    iframe.allowFullscreen = true;
    iframe.setAttribute('frameborder', '0');
    
    this.iframeWrapper.appendChild(iframe);
  }

  /**
   * Create Shopify hosted video
   */
  _createShopifyVideo(videoSrc) {
    if (!this.iframeWrapper) return;

    const video = document.createElement('video');
    video.src = videoSrc;
    video.autoplay = true;
    video.controls = true;
    video.playsInline = true;
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'contain';
    
    this.iframeWrapper.appendChild(video);
    
    video.play().catch(e => console.warn('Video autoplay failed:', e));
  }

  /**
   * Stop and clean up video
   */
  _stopVideo() {
    if (this.iframeWrapper) {
      // Stop any video by removing content
      const video = this.iframeWrapper.querySelector('video');
      if (video) {
        video.pause();
        video.src = '';
      }
      this.iframeWrapper.innerHTML = '';
    }
  }

  /**
   * Handle escape key
   */
  _handleEscape(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  /**
   * Handle focus trap keyboard events
   */
  _handleFocusTrap(e) {
    if (e.key !== 'Tab' || !this.modal) return;

    const focusableElements = this.modal.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    const focusableArray = Array.from(focusableElements).filter(el => el.offsetParent !== null);
    
    if (focusableArray.length === 0) return;

    const firstFocusable = focusableArray[0];
    const lastFocusable = focusableArray[focusableArray.length - 1];

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstFocusable || document.activeElement === this.modal) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  /**
   * Set inert attribute on all page content except the modal
   */
  _setPageInert(inert) {
    const bodyChildren = document.body.children;
    
    for (let i = 0; i < bodyChildren.length; i++) {
      const child = bodyChildren[i];
      
      // Skip the modal itself, scripts, and style tags
      if (child === this.modal || 
          child.tagName === 'SCRIPT' || 
          child.tagName === 'STYLE' ||
          child.tagName === 'LINK') {
        continue;
      }
      
      // Check if modal is a descendant of this child
      if (child.contains(this.modal)) {
        this._setAncestorSiblingsInert(child, inert);
        continue;
      }
      
      if (inert) {
        child.setAttribute('inert', '');
        child.setAttribute('aria-hidden', 'true');
      } else {
        child.removeAttribute('inert');
        child.removeAttribute('aria-hidden');
      }
    }
  }

  /**
   * Set inert on siblings of the modal's ancestor chain
   */
  _setAncestorSiblingsInert(container, inert) {
    let current = this.modal;
    
    while (current && current !== container && current !== document.body) {
      const parent = current.parentElement;
      if (!parent) break;
      
      for (let i = 0; i < parent.children.length; i++) {
        const sibling = parent.children[i];
        if (sibling === current || 
            sibling.tagName === 'SCRIPT' || 
            sibling.tagName === 'STYLE') {
          continue;
        }
        
        if (inert) {
          sibling.setAttribute('inert', '');
          sibling.setAttribute('aria-hidden', 'true');
        } else {
          sibling.removeAttribute('inert');
          sibling.removeAttribute('aria-hidden');
        }
      }
      
      current = parent;
    }
  }
}

// Create singleton instance
window.VideoModal = new VideoModalManager();
